import { useMutation } from '@tanstack/react-query';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { parseConsultationType } from '@/features/booking/shared/model/useBooking';
import {
    bookingDraftAtom,
    bookingErrorAtom,
    hasBlockingConsultationAtom,
} from '@/features/booking/shared/model/bookingState';
import { R } from '@/features/booking/resources/Texts.resx';
import { OpenVideoConsultationStatusEnum } from '@/shared/enums';
import { FetchError } from '@/shared/fetch';
import {
    IBaseOpenConsultationRequest,
    IOpenMessageConsultationRequest,
    IOpenVideoConsultationRequest,
    IOpenVideoConsultationResult,
} from '@/shared/interfaces';

import { createVideoConsultation } from '@/features/booking/shared/api/createVideoConsultation';
import { createMessageConsultation } from '@/features/booking/shared/api/createMessageConsultation';
import { latestCreateConsultationCallAtom } from '@/features/booking/shared/model/latestActions';
import { AiNurseStep } from '@/features/booking/shared/model/types';
import {
    UrlState,
    useUrlState,
    QueryKeys,
} from '@/features/booking/shared/model/useUrlState';
import { useTimeslots } from '@/features/booking/shared/model/useTimeslots';

export const useCreateConsultation = (userUid: string) => {
    const setLatestCreateConsultationCall = useSetAtom(
        latestCreateConsultationCallAtom,
    );
    const [_, setErrorMessage] = useAtom(bookingErrorAtom);
    const draftState = useAtomValue(bookingDraftAtom);
    const [__, setBlockingConsultation] = useAtom(hasBlockingConsultationAtom);

    const { urlState, updateUrl } = useUrlState();
    const { refetch: refetchTimeslots } = useTimeslots({
        consultationType: parseConsultationType(urlState.consultationType),
        performInitialFetch: false,
    });

    const videoMutation = useMutation({
        mutationFn: createVideoConsultation,
        onSuccess: (response) => {
            const updatedUrlState = {
                ...urlState,
                [QueryKeys.ConsultationUid]: response.NewConsultationGuid,
            };
            updateUrl(updatedUrlState, true);

            if (response.Status === 0 && response.NewConsultationGuid) {
                if (response.RequiresPayment) {
                    window.location.href =
                        window.location.origin +
                        '/payment/plan?consultation=' +
                        response.NewConsultationGuid;
                    return;
                }

                let redirectUrl: string;

                if (draftState?.queue === 'true') {
                    redirectUrl =
                        window.location.origin +
                        '/consultations/' +
                        response.NewConsultationGuid +
                        '/videocall';
                } else {
                    const scheduledDateParam =
                        '?scheduled=' +
                        encodeURIComponent(draftState?.timeslot);

                    redirectUrl =
                        window.location.origin +
                        '/consultations/video/' +
                        response.NewConsultationGuid +
                        '/scheduled-date' +
                        scheduledDateParam;
                }

                window.location.href = redirectUrl;
                return;
            } else {
                setErrorMessage(R.ErrorSchedulingConsultation);
            }
        },
        onError: async (error: FetchError<IOpenVideoConsultationResult>) => {
            const httpStatus = error.status;
            const result = error.data;
            switch (httpStatus) {
                case 402:
                    setErrorMessage(R.InvalidPayment);
                    break;
                case 409:
                    if (
                        [
                            OpenVideoConsultationStatusEnum.TimeSlotOverbooked,
                            OpenVideoConsultationStatusEnum.NoDoctorsAvailable,
                        ].includes(result.Status)
                    ) {
                        setErrorMessage(R.TimeslotUnavailable);
                        refetchTimeslots();
                        return;
                    }

                    if (
                        result.Status ===
                        OpenVideoConsultationStatusEnum.AlreadyHasUpcoming
                    ) {
                        setBlockingConsultation(true);
                    }
                    return;
                default:
                    setErrorMessage(R.ErrorSchedulingConsultation);
                    return;
            }
        },
    });

    const messageMutation = useMutation({
        mutationFn: createMessageConsultation,
        onSuccess: (response) => {
            const updatedUrlState = {
                ...urlState,
                [QueryKeys.ConsultationUid]: response.ConsultationGuid,
                [QueryKeys.MessageUid]: response.ConsultationMessageGuid,
            };
            updateUrl(updatedUrlState, true);

            if (response.RequiresPayment) {
                window.location.href =
                    window.location.origin +
                    '/payment/plan?consultation=' +
                    response.ConsultationGuid;
            } else {
                window.location.href =
                    window.location.origin +
                    '/consultations/' +
                    response.ConsultationGuid +
                    '/sentemail';
            }
            return;
        },
        onError: (error: any) => {
            const status = error?.response?.status;
            switch (status) {
                case 402:
                    setErrorMessage(R.InvalidPayment);
                    break;
                default:
                    setErrorMessage(R.ErrorSchedulingConsultation);
                    return;
            }
        },
    });

    const getBaseConsultationRequest = async (
        updatedUrlState: UrlState,
    ): Promise<IBaseOpenConsultationRequest> => {
        const isChildSelected = urlState.patientUid != userUid;

        return {
            OnBehalfUID: isChildSelected ? updatedUrlState.patientUid : null,
            ConsultationType: parseConsultationType(
                updatedUrlState.consultationType,
            ),
            NotifyOwnDoctor: updatedUrlState.notifyOwnDoctor === 'true',
            MobileNumber: draftState?.phone,
            Subject: draftState?.subject,
            Category: draftState?.category,
            OnBehalfOfPetID: updatedUrlState.petId,
            QuestionnaireSubmissionId: urlState.submissionId,
        };
    };

    const openVideoConsultation = async ({ isQueue }: { isQueue: boolean }) => {
        setLatestCreateConsultationCall(new Date());

        const baseRequest = await getBaseConsultationRequest(urlState);
        const request: IOpenVideoConsultationRequest = {
            ...baseRequest,
            Subject: baseRequest.Subject ?? 'Videokonsultation',
            ConsultationUid: urlState.consultationUid,
            FileUids: draftState?.attachmentUids?.split(','),
            PreferredCaregiverUid: draftState?.preferredCaregiverUid,
        };

        if (draftState?.timeslot && !isQueue) {
            request.ScheduledOn = new Date(draftState?.timeslot);
        }

        videoMutation.mutate(request);
    };

    const openMessageConsultation = async () => {
        setLatestCreateConsultationCall(new Date());

        const baseRequest = await getBaseConsultationRequest(urlState);
        const request: IOpenMessageConsultationRequest = {
            ...baseRequest,
            Message: draftState?.message,
            FileUids: draftState?.attachmentUids?.split(','),
            AiNurseInvolved: draftState?.aiNurseStep === AiNurseStep.Completed,
            MessageUid: urlState[QueryKeys.MessageUid],
            ConsultationUid: urlState[QueryKeys.ConsultationUid],
        };

        messageMutation.mutate(request);
    };

    return {
        openVideoConsultation,
        openMessageConsultation,
    };
};
